/* eslint-disable no-restricted-globals */
import i18n from 'i18next';

const isEmptyValue = (value) => value === undefined || value === '';

export const regexValidator = (regex, message) => (value) => {
  if (isEmptyValue(value)) return true;

  const formattedValue = Array.isArray(value) ? value.join(',') : value;
  if (formattedValue.match(regex)) return true;

  return message || i18n.t('валидация.regex', { regex });
};

export const maxLengthValidator = (value, message) => ({
  value,
  message: message || i18n.t('валидация.длина.максимум', { count: value }),
});

export const minLengthValidator = (value, message) => ({
  value,
  message: message || i18n.t('валидация.длина.минимум', { count: value }),
});

export const lteValidator = (value, message) => ({
  value,
  message: message || i18n.t('валидация.число.меньше или равно', { value }),
});

export const gteValidator = (value, message) => ({
  value,
  message: message || i18n.t('валидация.число.больше или равно', { value }),
});

export const ltValidator = (limit, message) => (value) => {
  if (isEmptyValue(value)) return true;
  if (value >= limit) return message || i18n.t('валидация.число.меньше', { value: limit });
  return true;
};
export const gtValidator = (limit, message) => (value) => {
  if (isEmptyValue(value)) return true;
  if (value <= limit) return message || i18n.t('валидация.число.больше', { value: limit });
  return true;
};
export const rangeValidator = ({
  left,
  right,
  context,
}, message) => (value) => {
  if (isEmptyValue(value)) return true;
  const STRICT_ENUM = ['none', 'left', 'right', 'both'];
  if (left === undefined || right === undefined || context === undefined) {
    throw new Error(`left, right and context thresholds are required in rangeValidator, given: ${JSON.stringify({
      left,
      right,
      context,
    })}`);
  }
  if (!STRICT_ENUM.includes(context)) throw new Error(`available values for context are [${STRICT_ENUM.join(', ')}] in rangeValidator, given: ${context}`);
  if (context === 'none') {
    if (value <= left) return message || i18n.t('валидация.число.диапазон', { left, right, context });
    if (value >= right) return message || i18n.t('валидация.число.диапазон', { left, right, context });
  }
  if (context === 'left') {
    if (value <= left) return message || i18n.t('валидация.число.диапазон', { left, right, context });
    if (value > right) return message || i18n.t('валидация.число.диапазон', { left, right, context });
  }
  if (context === 'right') {
    if (value < left) return message || i18n.t('валидация.число.диапазон', { left, right, context });
    if (value >= right) return message || i18n.t('валидация.число.диапазон', { left, right, context });
  }
  if (context === 'both') {
    if (value < left) return message || i18n.t('валидация.число.диапазон', { left, right, context });
    if (value > right) return message || i18n.t('валидация.число.диапазон', { left, right, context });
  }
  return true;
};

export const requiredValidator = (message) => message ?? i18n.t('валидация.обязательно для заполнения');

export const UUID_PATTERN = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

export const UUIDValidator = (message) => (value) => regexValidator(
  UUID_PATTERN,
  message || i18n.t('валидация.uuid'),
)(value);

export const multipleUUIDValidator = (value) => {
  let formattedValue = Array.isArray(value) ? value : [];

  if (value && typeof value === 'string') {
    formattedValue = value?.split(',');
  }

  formattedValue.filter((v) => v?.length).map((v) => v.trim());

  if (!formattedValue?.length) return true;
  const messages = [];
  const isValid = formattedValue.every((item) => {
    const result = UUIDValidator(item);
    messages.push(result);
    if (result) return true;
    return result;
  });
  if (!isValid) return messages;
  return true;
};

export const integerValidator = (message) => (value) => {
  if (isEmptyValue(value)) return true;
  if (Number.isInteger(Number(value))) return true;
  return message || i18n.t('валидация.число.целое');
};

export const numberValidator = (message) => (value) => {
  if (isEmptyValue(value)) return true;
  if (!isNaN(value)) return true;
  return message || i18n.t('валидация.число.только числа');
};

export default {
  lte: lteValidator,
  gte: gteValidator,
  lt: ltValidator,
  gt: gtValidator,
  range: rangeValidator,
  maxLength: maxLengthValidator,
  minLength: minLengthValidator,
  number: numberValidator,
  integer: integerValidator,
  regex: regexValidator,
  uuid: UUIDValidator,
  required: requiredValidator,
};
